import * as XLSX from "xlsx";

/** Column headers matching the vehicle detail / bank Excel layout. */
export const UPLOAD_SAMPLE_COLUMNS = [
  "Registration Numbers",
  "1st Confirmer Name",
  "1st Confirmer No",
  "2nd Confirmer Name",
  "2nd Confirmer No",
  "3rd Confirmer Name",
  "3rd Confirmer No",
  "Loan Number",
  "Make",
  "Chasis Number",
  "Engine Number",
  "EMI",
  "POS",
  "Bucket",
  "Customer Name",
  "Address",
  "Branch",
  "Sec 17",
  "Seasoning",
  "TBR",
  "Allocation",
  "Model",
];

const FIRST_NAMES = [
  "AMIT",
  "RAHUL",
  "PRIYA",
  "NEHA",
  "VIKAS",
  "ANITA",
  "ROHIT",
  "KAVITA",
  "SANJAY",
  "POOJA",
];
const LAST_NAMES = [
  "SHARMA",
  "SINGH",
  "PATEL",
  "VERMA",
  "GUPTA",
  "YADAV",
  "KUMAR",
  "MEHTA",
  "JAIN",
  "MISHRA",
];
const MAKES = ["TATA", "MARUTI", "HYUNDAI", "HONDA", "MAHINDRA", "TOYOTA", "KIA"];
const MODELS = ["NEXON", "SWIFT", "CRETA", "CITY", "XUV700", "INNOVA", "SELTOS"];
const BRANCHES = ["Delhi", "Andheri", "Pune", "Jaipur", "Lucknow", "Chennai"];
const ADDRESSES = [
  "Delhi NCR",
  "Mumbai",
  "Pune",
  "Ahmedabad",
  "Bengaluru",
  "Hyderabad",
];
const STATES = ["UP", "MH", "DL", "KA", "TN", "GJ", "RJ", "HR"];
const LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const BUCKETS = ["0", "1", "2", "3", "X"];
const SEASONING = ["1", "3", "6", "12", "18", "24"];
const YES_NO = ["Yes", "No"];
const ALLOCATIONS = ["Agency A", "Agency B", "Inhouse", "Legal"];

function randInt(max) {
  return Math.floor(Math.random() * max);
}

function pick(list) {
  return list[randInt(list.length)];
}

function randomLetters(count) {
  let out = "";
  for (let i = 0; i < count; i += 1) out += LETTERS[randInt(LETTERS.length)];
  return out;
}

function randomDigits(count) {
  let out = "";
  for (let i = 0; i < count; i += 1) out += String(randInt(10));
  return out;
}

function uniqueToken(rowIndex) {
  return `${Date.now().toString(36)}${rowIndex}${randInt(90) + 10}`;
}

function generateRegistrationNumber(rowIndex = 0) {
  const state = pick(STATES);
  const rto = String(1 + ((randInt(98) + rowIndex) % 99)).padStart(2, "0");
  const series = randomLetters(2);
  const num = String(1000 + ((rowIndex * 137 + randInt(8000)) % 9000));
  return `${state} ${rto} ${series} ${num}`;
}

function generatePersonName() {
  return `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
}

function generateMobile() {
  return `${6 + randInt(4)}${randomDigits(9)}`;
}

function generateLoanNumber(rowIndex) {
  return `VL${uniqueToken(rowIndex)}`.slice(0, 18).toUpperCase();
}

function generateChassisNumber(rowIndex) {
  return `MAT${uniqueToken(rowIndex)}${randomLetters(6)}`.replace(/[^A-Z0-9]/gi, "").slice(0, 17).toUpperCase();
}

function generateEngineNumber(rowIndex) {
  return `EN${uniqueToken(rowIndex)}`.slice(0, 12).toUpperCase();
}

export function generateSampleFieldValue(field, rowIndex = 0, previous = "") {
  const generators = {
    "Registration Numbers": () => generateRegistrationNumber(rowIndex),
    "1st Confirmer Name": generatePersonName,
    "2nd Confirmer Name": generatePersonName,
    "3rd Confirmer Name": generatePersonName,
    "1st Confirmer No": generateMobile,
    "2nd Confirmer No": generateMobile,
    "3rd Confirmer No": generateMobile,
    "Loan Number": () => generateLoanNumber(rowIndex),
    Make: () => pick(MAKES),
    "Chasis Number": () => generateChassisNumber(rowIndex),
    "Engine Number": () => generateEngineNumber(rowIndex),
    EMI: () => String(5000 + randInt(20000)),
    POS: () => String(20000 + randInt(180000)),
    Bucket: () => pick(BUCKETS),
    "Customer Name": generatePersonName,
    Address: () => pick(ADDRESSES),
    Branch: () => pick(BRANCHES),
    "Sec 17": () => pick(YES_NO),
    Seasoning: () => pick(SEASONING),
    TBR: () => pick(YES_NO),
    Allocation: () => pick(ALLOCATIONS),
    Model: () => pick(MODELS),
  };

  const generate = generators[field];
  if (!generate) return previous ?? "";

  let next = generate();
  for (let attempt = 0; attempt < 8 && String(next) === String(previous ?? ""); attempt += 1) {
    next = generate();
  }
  if (String(next) === String(previous ?? "")) {
    next = `${next}-${rowIndex + 1}`;
  }
  return next;
}

/** Example rows shown in the upload screen preview. */
export const UPLOAD_SAMPLE_ROWS = [
  {
    "Registration Numbers": "UP 81 BK 5151",
    "1st Confirmer Name": "Rajesh Kumar",
    "1st Confirmer No": "9876543210",
    "2nd Confirmer Name": "Amit Sharma",
    "2nd Confirmer No": "9876543211",
    "3rd Confirmer Name": "",
    "3rd Confirmer No": "",
    "Loan Number": "VL0000000020295",
    Make: "Xuv 500 R FWD W10",
    "Chasis Number": "MA1YU2HJUF6G13688",
    "Engine Number": "HJF4G15571",
    EMI: "0",
    POS: "0",
    Bucket: "1",
    "Customer Name": "SHIVANSH SINGH",
    Address: "Delhi NCR",
    Branch: "Delhi",
    "Sec 17": "",
    Seasoning: "",
    TBR: "",
    Allocation: "",
    Model: "2015",
  },
  {
    "Registration Numbers": "MH 12 AB 1807",
    "1st Confirmer Name": "",
    "1st Confirmer No": "",
    "2nd Confirmer Name": "",
    "2nd Confirmer No": "",
    "3rd Confirmer Name": "",
    "3rd Confirmer No": "",
    "Loan Number": "LAN09887665324",
    Make: "TATA",
    "Chasis Number": "MAT123456789012",
    "Engine Number": "ENG1234567",
    EMI: "12500",
    POS: "85000",
    Bucket: "12",
    "Customer Name": "RAHUL SHARMA",
    Address: "Mumbai",
    Branch: "Andheri",
    "Sec 17": "",
    Seasoning: "",
    TBR: "",
    Allocation: "",
    Model: "NEXON",
  },
];

export function buildUploadSampleRows(changeField = "") {
  const field = String(changeField || "").trim();
  if (!field || !UPLOAD_SAMPLE_COLUMNS.includes(field)) {
    return UPLOAD_SAMPLE_ROWS;
  }
  return UPLOAD_SAMPLE_ROWS.map((row, index) => ({
    ...row,
    [field]: generateSampleFieldValue(field, index, row[field]),
  }));
}

export function downloadUploadSampleExcel(
  filename = "FastRecovery-upload-sample.xlsx",
  changeField = ""
) {
  const rows = buildUploadSampleRows(changeField);
  const sheetData = [
    UPLOAD_SAMPLE_COLUMNS,
    ...rows.map((row) => UPLOAD_SAMPLE_COLUMNS.map((col) => row[col] ?? "")),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  worksheet["!cols"] = UPLOAD_SAMPLE_COLUMNS.map((col) => ({
    wch: Math.min(28, Math.max(col.length + 2, 14)),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Cases");
  XLSX.writeFile(workbook, filename);
}
